<template>
  <transition name="modal" appear>
    <div v-if="visible" class="login-container">
      <div class="login-overlay" @click="goBack"></div>
      <div class="login-box">
      <div class="login-header">
        <h2 class="login-title">登录</h2>
        <button class="close-btn" @click="goBack">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
      <form @submit.prevent="handleLogin" class="login-form" novalidate>
        <div class="form-group">
          <label for="username">账号</label>
          <input
            type="text"
            id="username"
            v-model="loginForm.username"
            placeholder="6-18位大小写字母和数字"
            :class="{ 'error': errors.username }"
          />
          <span v-if="errors.username" class="error-message">{{ errors.username }}</span>
        </div>

        <div class="form-group">
          <label for="password">密码</label>
          <div class="password-input-wrapper">
            <input
              :type="showPassword ? 'text' : 'password'"
              id="password"
              v-model="loginForm.password"
              placeholder="8-16位，包含大小写字母和数字，可含特殊字符"
              :class="{ 'error': errors.password }"
            />
            <button type="button" class="toggle-password-btn" @click="showPassword = !showPassword" tabindex="-1">
              <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
            </button>
          </div>
          <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
        </div>

        <transition name="captcha-fade">
          <div class="form-group captcha-group" v-if="useTwofa || useEmail">
            <transition name="mode-switch" mode="out-in">
              <div :key="useTwofa ? 'twofa' : 'email'">
                <label v-if="useTwofa" for="twofa">两步验证码</label>
                <label v-else for="emailcode">邮箱验证码</label>
                
                <div class="captcha-container" v-if="useTwofa">
                  <input type="text" id="twofa" v-model="loginForm.twofaCode" placeholder="请输入两步验证码" :class="{ 'error': errors.captcha }" />
                </div>
                <div class="captcha-container" v-else>
                  <input type="text" id="emailcode" v-model="loginForm.emailCode" placeholder="请输入邮箱验证码" :class="{ 'error': errors.captcha }" />
                  <button type="button" class="login-btn login-btn-secondary" @click="sendEmailLoginCode" :disabled="emailSending">
                    <i :class="emailSending ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-paper-plane'"></i>
                  </button>
                </div>
              </div>
            </transition>
            <span v-if="errors.captcha" class="error-message">{{ errors.captcha }}</span>
          </div>
        </transition>

        <div class="login-actions">
          <button type="submit" class="login-btn login-btn-main" :disabled="isLoading">
            {{ isLoading ? '登录中...' : '登录' }}
          </button>
          <button 
            v-if="!showForgotPasswordModal" 
            type="button" 
            class="login-btn-switch" 
            @click="toggleMode" 
            :title="toggleLabel"
          >
            <i class="fa-solid fa-rotate"></i>
          </button>
        </div>

        <div class="form-links">
          <button @click="switchToRegister" type="button" class="link register-link">还没有账号？立即注册</button>
          <button @click="openForgotPassword" type="button" class="link forgot-password-link">忘记密码？</button>
        </div>
        
        <!-- 免责声明提示 -->
        <div class="disclaimer-notice">
          <p>登录即代表您已经详细阅读并同意了我们的
            <router-link to="/mianze" class="disclaimer-link" @click="goBack">《免责声明》</router-link>
            和
            <router-link to="/rules" class="disclaimer-link" @click="goBack">《文章规则》</router-link>
          </p>
        </div>
      </form>

      <!-- 第三方登录 -->
      <div class="third-party-login">
        <div class="divider-text"><span>第三方登录</span></div>
        <div class="third-party-btns">
          <button type="button" class="qq-login-btn" @click="handleQQLogin" :disabled="qqLoading">
            <i class="fa-brands fa-qq"></i>
            <span>{{ qqLoading ? '跳转中...' : 'QQ登录' }}</span>
          </button>
          <button type="button" class="wx-login-btn" @click="handleWxLogin" :disabled="wxLoading">
            <i class="fa-brands fa-weixin"></i>
            <span>{{ wxLoading ? '加载中...' : '微信登录' }}</span>
          </button>
        </div>
      </div>

      <!-- 微信扫码登录弹窗 -->
      <teleport to="body">
        <WxQrLogin :visible="wxQrVisible" mode="login" @close="wxQrVisible = false" @success="onWxLoginSuccess" @unbound="onWxUnbound" />
      </teleport>

      <!-- 安全滑块验证码弹窗 -->
      <SlideCaptcha v-model:visible="captchaVisible" @success="onCaptchaSuccess" />
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, reactive, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import serverUrl from '@/assets/BaseServerUrl.js'
import { message, messageBox } from '@/utils/ui-service'
import { isRateLimitError } from '@/utils/httpErrorUtils.js'
import eventBus from '@/utils/eventBus'
import SlideCaptcha from '@/components/SlideCaptcha.vue'
import WxQrLogin from '@/components/WxQrLogin.vue'

export default {
  name: 'LoginPage',
  components: {
    SlideCaptcha,
    WxQrLogin
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['close', 'switch-to-register'],
  setup(props, { emit }) {
    const isLoading = ref(false)
    const router = useRouter()
    const captchaVisible = ref(false)
    const verifiedCaptchaToken = ref('')
    const captchaPurpose = ref('login')
    const pendingEmail = ref('')
    const showPassword = ref(false)
    
    const loginForm = reactive({
      username: '',
      password: '',
      captcha: '',
      twofaCode: '',
      emailCode: ''
    })
    
    const errors = reactive({
      username: '',
      password: '',
      captcha: ''
    })
    
    const usernameRegex = /^[a-zA-Z0-9]{6,18}$/
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[\x21-\x7E]{8,16}$/
    const specialUsernames = ['umi', 'shen']

    const validateLoginForm = () => {
      let isValid = true
      errors.username = ''
      errors.password = ''
      errors.captcha = ''

      if (!loginForm.username) {
        errors.username = '请输入账号'
        isValid = false
      } else if (!usernameRegex.test(loginForm.username)) {
        errors.username = '账号格式为6-18位大小写字母和数字'
        if (!specialUsernames.includes(loginForm.username)) {
          isValid = false
        }
      }

      if (!loginForm.password) {
        errors.password = '请输入密码'
        isValid = false
      } else if (!passwordRegex.test(loginForm.password)) {
        errors.password = '密码需8-16位，包含大小写字母 and 数字，可含常见特殊字符'
        isValid = false
      }

      if (useTwofa.value) {
        if (!loginForm.twofaCode) { errors.captcha = '请输入两步验证码'; isValid = false }
      } else if (useEmail.value) {
        if (!loginForm.emailCode) { errors.captcha = '请输入邮箱验证码'; isValid = false }
      }

      return isValid
    }

    const useTwofa = ref(false)
    const useEmail = ref(false)
    const emailSending = ref(false)

    // 关闭模态框
    const goBack = () => {
      emit('close')
    }
    
    // 切换到注册模态框
    const switchToRegister = () => {
      emit('switch-to-register')
    }

    const openForgotPassword = () => {
      eventBus.emit('show-forgot-password')
    }
    
    // 处理登录
    const handleLogin = async () => {
      if (!validateLoginForm()) {
        return
      }

      if (!useTwofa.value && !useEmail.value && !verifiedCaptchaToken.value) {
        captchaPurpose.value = 'login'
        captchaVisible.value = true
        return
      }

      isLoading.value = true
      
      try {
        const payload = { username: loginForm.username, password: loginForm.password }
        if (useTwofa.value) {
          payload.twofaMode = true
          payload.twofaCode = loginForm.twofaCode
        } else if (useEmail.value) {
          payload.emailMode = true
          payload.emailCode = loginForm.emailCode
        } else {
          payload.captcha = verifiedCaptchaToken.value
        }
        const response = await axios.post(`${serverUrl}/api/login`, payload)
        
        if (response.data.message === '登录成功！' || response.data.message === '登录成功') {
          const userData = response.data.data
          localStorage.setItem('userInfo', JSON.stringify(userData))
          localStorage.setItem('userToken', userData.token)
          localStorage.setItem('lastLoginTime', new Date().toISOString())
          localStorage.setItem('isLoggedIn', 'true')
          emit('close')
          try {
            const redirectPath = localStorage.getItem('postLoginRedirect')
            if (redirectPath) {
              localStorage.removeItem('postLoginRedirect')
              router.push(redirectPath)
            } else {
              router.push('/')
            }
          } catch (e) {
            router.push('/')
          }
          setTimeout(() => { window.location.reload() }, 100)
        } else {
          message.error(response.data.message || '登录失败')
          verifiedCaptchaToken.value = ''
          if (response.data.error && String(response.data.error).includes('已开启两步验证')) {
            useTwofa.value = true
          }
        }
      } catch (error) {
        console.error('登录错误:', error)
        verifiedCaptchaToken.value = ''
        if (error.response && error.response.data && error.response.data.error) {
          const msg = error.response.data.error
          
          if (error.response.data.bannedUntil) {
             messageBox.alert(msg, '账号封禁通知', {
                confirmButtonText: '确定',
                type: 'error',
                center: true
             })
          } else {
             message.error(msg)
          }

          if (String(msg).includes('已开启两步验证')) { useTwofa.value = true }
        } else if (error.response) {
          message.error(`登录失败：${error.response.status} ${error.response.statusText}`)
        } else if (error.request) {
          message.error('登录失败，请检查网络连接')
        } else {
          message.error('登录失败，发生未知错误')
        }
      } finally {
        isLoading.value = false
      }
    }

    const onCaptchaSuccess = (token) => {
      if (captchaPurpose.value === 'login') {
        verifiedCaptchaToken.value = token
        handleLogin()
      } else if (captchaPurpose.value === 'sendEmail') {
        executeSendEmailLoginCode(pendingEmail.value, token)
      }
    }

    const toggleMode = () => {
      if (useTwofa.value) {
        useTwofa.value = false
        useEmail.value = true
      } else if (useEmail.value) {
        useEmail.value = false
        useTwofa.value = false
      } else {
        useTwofa.value = true
        useEmail.value = false
      }
      errors.captcha = ''
      loginForm.captcha = ''
      loginForm.twofaCode = ''
      loginForm.emailCode = ''
      verifiedCaptchaToken.value = ''
    }

    const toggleLabel = computed(() => {
      if (useTwofa.value) return '切换邮箱验证'
      if (useEmail.value) return '切换图形验证'
      return '切换两步验证'
    })

    const sendEmailLoginCode = async () => {
      if (!loginForm.username) { message.error('请先输入账号'); return }
      emailSending.value = true
      try {
        const info = await axios.post(`${serverUrl}/api/auth/get-user-info`, { username: loginForm.username })
        const email = info?.data?.data?.email
        if (!email) { message.error('该账号未绑定邮箱'); return }
        
        pendingEmail.value = email
        captchaPurpose.value = 'sendEmail'
        captchaVisible.value = true
      } catch (e) {
        if (!isRateLimitError(e)) {
          message.error(e.response?.data?.error || e.response?.data || '获取验证码失败，请刷新页面重试')
        }
      } finally {
        emailSending.value = false
      }
    }

    const executeSendEmailLoginCode = async (email, captchaToken) => {
      emailSending.value = true
      try {
        const response = await axios.post(`${serverUrl}/sendVerificationCode`, { 
          email,
          captcha: captchaToken
        })
        if (response.status === 200) {
          message.success('验证码已发送，请查收邮箱')
        } else {
          message.error(response.data || '发送验证码失败')
        }
      } catch (e) {
        if (!isRateLimitError(e)) {
          message.error(e.response?.data?.error || e.response?.data || '发送验证码失败，请重试')
        }
      } finally {
        emailSending.value = false
        pendingEmail.value = ''
      }
    }

    // 输入时清除错误提示
    watch(() => loginForm.username, () => { errors.username = '' })
    watch(() => loginForm.password, () => { errors.password = '' })
    watch(() => loginForm.twofaCode, () => { if (useTwofa.value) errors.captcha = '' })
    watch(() => loginForm.emailCode, () => { if (useEmail.value) errors.captcha = '' })

    // 监听visible属性变化，当模态框显示时
    watch(() => props.visible, (newVisible) => {
      if (newVisible) {
        // 清空表单数据
        Object.keys(loginForm).forEach(key => {
          loginForm[key] = ''
        })
        loginForm.emailCode = ''
        verifiedCaptchaToken.value = ''
      }
    })
    
    const showForgotPasswordModal = ref(false)
    const qqLoading = ref(false)
    const wxLoading = ref(false)
    const wxQrVisible = ref(false)

    // QQ 登录：获取授权URL并跳转
    const handleQQLogin = async () => {
      qqLoading.value = true
      try {
        const response = await axios.get(`${serverUrl}/api/auth/qq/authurl`)
        if (response.data.success && response.data.authUrl) {
          window.location.href = response.data.authUrl
        } else {
          message.error(response.data.error || 'QQ登录功能暂不可用')
        }
      } catch (error) {
        message.error(error?.response?.data?.error || 'QQ登录功能暂不可用，请稍后重试')
      } finally {
        qqLoading.value = false
      }
    }

    // 微信登录：打开扫码弹窗
    const handleWxLogin = () => {
      wxLoading.value = true
      wxQrVisible.value = true
      setTimeout(() => { wxLoading.value = false }, 300)
    }

    // 微信登录成功回调
    // WxQrLogin 组件内部已处理 localStorage 存储、成功提示展示 and 页面跳转
    // 这里不需要额外操作，让组件自行完成 1.5s 成功展示后再关闭弹窗
    const onWxLoginSuccess = () => {}

    const onWxUnbound = (bindToken) => {
      wxQrVisible.value = false
      localStorage.setItem('tempBindToken', bindToken)
      messageBox.confirm(
        '该微信账号暂未绑定云语账号，是否立即创建一个新账号并完成自动绑定？',
        '微信账号未绑定',
        {
          confirmButtonText: '立即注册并绑定',
          cancelButtonText: '取消',
          type: 'info'
        }
      ).then(() => {
        emit('close')
        emit('switch-to-register')
      }).catch(() => {
        localStorage.removeItem('tempBindToken')
      })
    }

    return {
      loginForm,
      errors,
      isLoading,
      useEmail,
      emailSending,
      handleLogin,
      useTwofa,
      toggleMode,
      toggleLabel,
      sendEmailLoginCode,
      executeSendEmailLoginCode,
      goBack,
      switchToRegister,
      openForgotPassword,
      showForgotPasswordModal,
      captchaVisible,
      verifiedCaptchaToken,
      captchaPurpose,
      pendingEmail,
      onCaptchaSuccess,
      showPassword,
      qqLoading,
      handleQQLogin,
      wxLoading,
      wxQrVisible,
      handleWxLogin,
      onWxLoginSuccess,
      onWxUnbound
    }
  }
}
</script>

<style scoped>
.login-container {
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  padding: 20px;
  box-sizing: border-box;
}

.login-overlay {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.5); /* 纯透明度过度，移除模糊 */
}

.login-box {
  background: rgba(255, 255, 255, 0.75); /* 增强背景不透明度，提升阅读感 */
  padding: 40px;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 420px;
  backdrop-filter: blur(15px) saturate(180%);
  position: relative;
  z-index: 10;
  box-sizing: border-box;
}

.dark-theme .login-box {
  background: rgba(28, 28, 30, 0.85);
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4);
}

.login-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 35px;
}

.login-title {
  text-align: center;
  margin: 0;
  color: #1d1d1f;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.5px;
  font-family: '爱点风雅黑', sans-serif;
}

.dark-theme .login-title {
  color: #f5f5f7;
}

.close-btn {
  position: absolute;
  top: -10px; right: -10px;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  font-size: 18px;
  color: #86868b;
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  width: 32px; height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: #1d1d1f;
  transform: rotate(90deg);
}

.dark-theme .close-btn {
  background: rgba(255, 255, 255, 0.1);
  color: #a1a1a6;
}

.dark-theme .close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}

/* 进场动画 */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-active .login-box,
.modal-leave-active .login-box {
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-enter-from .login-box {
  transform: scale(0.92) translateY(30px);
  opacity: 0;
}

.modal-leave-to .login-box {
  transform: scale(0.96) translateY(15px);
  opacity: 0;
}

.modal-enter-to .login-box,
.modal-leave-from .login-box {
  transform: scale(1) translateY(0);
  opacity: 1;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-group label {
  font-weight: 600;
  color: #424245;
  font-size: 14px;
  margin-left: 4px;
}

.dark-theme .form-group label {
  color: #a1a1a6;
}

.form-group input {
  padding: 14px 18px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.5);
  color: #1d1d1f;
  outline: none;
  box-sizing: border-box;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper input {
  width: 100%;
  padding-right: 44px;
}

.toggle-password-btn {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #86868b;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.toggle-password-btn:hover {
  color: #1d1d1f;
}

.dark-theme .toggle-password-btn:hover {
  color: #f5f5f7;
}

.dark-theme .form-group input {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f5f5f7;
}

.form-group input::placeholder {
  color: #a1a1a6;
  font-size: 14px;
}

.form-group input:focus {
  border-color: #4CAF50;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.15); /* 绿色焦点 */
}

.dark-theme .form-group input:focus {
  background: rgba(255, 255, 255, 0.1);
  border-color: #4CAF50;
}

.captcha-group .captcha-container {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 10px;
}

.captcha-container input {
  flex: 1;
  min-width: 0;
}

.captcha-canvas {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 48px;
  background: white;
}

.dark-theme .captcha-canvas {
  border-color: rgba(255, 255, 255, 0.1);
  opacity: 0.8;
}

.dark-theme .login-btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: #4CAF50;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.login-btn {
  border: none;
  padding: 16px 20px;
  border-radius: 14px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-btn-main {
  background: linear-gradient(135deg, #4CAF50 0%, #0071e3 100%); /* 绿蓝渐变 */
  color: white;
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.3);
  width: 100%;
  position: relative;
  overflow: hidden;
}

.login-btn-switch {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 42px;
  height: 42px;
  background: rgba(255, 255, 255, 0.25);
  border: none;
  border-top-left-radius: 100%;
  border-bottom-right-radius: 14px; /* 匹配父级圆角，修复直角问题 */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: 12;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0; /* 移除偏移，修复不居中问题 */
}

.login-btn-switch:hover {
  background: rgba(255, 255, 255, 0.4);
  width: 46px;
  height: 46px;
}

.login-btn-switch i {
  font-size: 15px;
  margin-top: 4px; /* 视觉微调：由于扇形区域，稍微向下偏一点点看起来更像在扇形的中心 */
  margin-left: 4px; /* 视觉微调：稍微向右偏，修复“靠左”感 */
}

.login-actions { 
  display: flex; 
  position: relative; 
  margin-top: 12px; 
}

.form-links {
  display: flex;
  justify-content: space-between;
  margin-top: 25px;
  padding: 0 4px;
}

.link {
  color: #4CAF50;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  background: none;
  border: none;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.link:hover {
  opacity: 0.7;
  text-decoration: underline;
}

/* 模式切换动画 */
.mode-switch-enter-active, .mode-switch-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.mode-switch-enter-from {
  opacity: 0;
  transform: translateX(15px);
}
.mode-switch-leave-to {
  opacity: 0;
  transform: translateX(-15px);
}

/* 验证码框平滑收缩与展开过渡动画 */
.captcha-fade-enter-active,
.captcha-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 110px;
  opacity: 1;
  overflow: hidden;
}
.captcha-fade-enter-from,
.captcha-fade-leave-to {
  max-height: 0;
  opacity: 0;
}

/* 免责声明提示样式 */
.disclaimer-notice {
  margin-top: 25px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  text-align: center;
}

.disclaimer-notice p {
  margin: 0;
  font-size: 12px;
  color: #86868b;
  line-height: 1.6;
}

.disclaimer-link {
  color: #4CAF50;
  text-decoration: none;
  font-weight: 600;
}

.dark-theme .disclaimer-notice {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

@media (max-width: 480px) {
  .login-container {
    padding: 0;
  }
  .login-box {
    padding: 0;
    border-radius: 0;
    height: 100vh;
    max-height: 100vh;
    border: none;
    max-width: 100vw;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
  .login-header {
    justify-content: space-between;
    padding: 20px 25px;
    background: rgba(255, 255, 255, 0.2);
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    margin-bottom: 0;
  }
  .dark-theme .login-header {
    background: rgba(0, 0, 0, 0.2);
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }
  .login-title {
    font-size: 26px;
    text-align: left;
  }
  .close-btn {
    position: static;
  }
  .login-form {
    padding: 25px 20px;
    overflow-y: auto;
  }
  
  .modal-overlay {
    padding: 0;
  }
  .modal {
    padding: 0;
    border-radius: 0;
    height: 100vh;
    max-height: 100vh;
    border: none;
    max-width: 100vw;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
  .modal-header {
    justify-content: space-between;
    padding: 20px 25px;
    margin-bottom: 0;
    background: rgba(255, 255, 255, 0.2);
  }
  .dark-theme .modal-header {
    background: rgba(0, 0, 0, 0.2);
  }
  .modal-header h3 {
    font-size: 22px;
  }
  .modal-header .close-btn {
    position: static;
  }
  .forgot-password-step, .modal-actions, .resend-code {
    padding: 0 20px;
  }
  .forgot-password-step {
    padding-top: 25px;
  }
}

.form-group input.error {
  border-color: #ff3b30;
  background: rgba(255, 59, 48, 0.05);
}

.error-message {
  color: #ff3b30;
  font-size: 12px;
  margin-top: 4px;
  margin-left: 4px;
  font-weight: 500;
}

/* 第三方登录 */
.third-party-login {
  margin-top: 20px;
}

.divider-text {
  display: flex;
  align-items: center;
  text-align: center;
  margin-bottom: 16px;
}

.divider-text::before,
.divider-text::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
}

.divider-text span {
  padding: 0 14px;
  font-size: 12px;
  color: #86868b;
}

.dark-theme .divider-text::before,
.dark-theme .divider-text::after {
  background: rgba(255, 255, 255, 0.1);
}

.third-party-btns {
  display: flex;
  gap: 10px;
}

.third-party-btns button {
  flex: 1;
}

.qq-login-btn {
  width: 100%;
  padding: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  background: rgba(18, 183, 245, 0.08);
  color: #12b7f5;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.qq-login-btn:hover:not(:disabled) {
  background: rgba(18, 183, 245, 0.15);
  border-color: rgba(18, 183, 245, 0.3);
}

.qq-login-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dark-theme .qq-login-btn {
  background: rgba(18, 183, 245, 0.1);
  border-color: rgba(255, 255, 255, 0.1);
  color: #12b7f5;
}

.qq-login-btn i {
  font-size: 18px;
}

.wx-login-btn {
  width: 100%;
  padding: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  background: rgba(7, 193, 96, 0.08);
  color: #07c160;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.wx-login-btn:hover:not(:disabled) {
  background: rgba(7, 193, 96, 0.15);
  border-color: rgba(7, 193, 96, 0.3);
}

.wx-login-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dark-theme .wx-login-btn {
  background: rgba(7, 193, 96, 0.1);
  border-color: rgba(255, 255, 255, 0.1);
  color: #07c160;
}

.wx-login-btn i {
  font-size: 18px;
}
</style>
